import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  QrCode,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Smartphone,
} from "lucide-react";
import {
  createUnofficialWaInstance,
  getUnofficialWaStatus,
  getUnofficialWaQr,
} from "../../../../api/channelIntegrations";
import { AuthService } from "../../../../services/authservice";
import { showToast } from "../../../../utils/showToast";
import { cn } from "../../../../utils/cn";
import useIsMobile from "../../../../hooks/useIsMobile";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";

const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/+$/, "") + "/";

// Propósito del número al conectarlo: ambos / solo notificaciones / solo mesa
// de ayuda. Mantiene separado el uso desde el momento de la vinculación.
const PURPOSE_OPTIONS = [
  { value: "both", labelKey: "Ambos" },
  { value: "notifications", labelKey: "Notificaciones" },
  { value: "helpdesk", labelKey: "Mesa de ayuda" },
];

export default function QrCodeModal({
  isOpen,
  toggle,
  groups,
  onConnected,
  defaultPurpose = "both",
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [displayName, setDisplayName] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [purpose, setPurpose] = useState(defaultPurpose);
  const [qrBase64, setQrBase64] = useState(null);
  const [, setIntegrationId] = useState(null);
  const [, setInstanceName] = useState(null);
  // idle | creating | scanning | connected | error
  const [status, setStatus] = useState("idle");
  const eventSourceRef = useRef(null);
  const pollTimerRef = useRef(null);
  // Timer del cierre diferido tras "conectado" (1.5s). Se guarda para cancelarlo
  // si el modal se cierra/desmonta antes (evita onConnected/toggle post-unmount).
  const closeTimerRef = useRef(null);
  // Evita doble-completado si el SSE y el polling detectan "open" a la vez.
  const finishedRef = useRef(false);
  // Invalida una apertura de SSE que sigue esperando el token cuando el modal
  // se cierra/reinicia (cleanup) o se vuelve a pedir otra conexión.
  const sseSeqRef = useRef(0);

  const cleanup = useCallback(() => {
    sseSeqRef.current += 1;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Reset al cerrar; al abrir, arranca con el propósito solicitado por quien
  // abre el modal (p. ej. la mesa de ayuda lo abre con "helpdesk").
  useEffect(() => {
    if (!isOpen) {
      setDisplayName("");
      setSelectedGroupId("");
      setQrBase64(null);
      setIntegrationId(null);
      setInstanceName(null);
      setStatus("idle");
      finishedRef.current = false;
      cleanup();
      stopPolling();
    } else {
      setPurpose(defaultPurpose);
    }
  }, [isOpen, cleanup, stopPolling, defaultPurpose]);

  // Cleanup al desmontar
  useEffect(
    () => () => {
      cleanup();
      stopPolling();
    },
    [cleanup, stopPolling],
  );

  // Transición única a "conectado", disparada por el primer detector que gane
  // (SSE o polling). Idempotente vía finishedRef.
  const finishConnected = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    cleanup();
    stopPolling();
    setStatus("connected");
    showToast(t("WhatsApp conectado exitosamente"), true);
    // cleanup() ya corrió arriba (antes de agendar), así que este timer no se
    // auto-cancela; sí lo cancelan los efectos de reset/desmontaje vía cleanup().
    closeTimerRef.current = setTimeout(() => {
      onConnected?.();
      toggle();
    }, 1500);
  }, [cleanup, stopPolling, onConnected, toggle, t]);

  const connectSSE = useCallback(
    async (intId) => {
      cleanup();
      const seq = sseSeqRef.current;
      // EventSource no admite headers: el token va en la query, así que tiene que
      // estar vigente al abrir (con access de 1 h uno viejo daba 401 mudo).
      const token = (await AuthService.getValidAccessToken()) || "";
      if (seq !== sseSeqRef.current) return;
      const url = `${BFF_BASE_URL}channel-integrations/unofficial-wa/instances/${intId}/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "qr_update" && data.qrCode) {
            setQrBase64(data.qrCode);
            setStatus("scanning");
          }
          if (data.type === "connection_update" && data.state === "open") {
            finishConnected();
          }
          // 'close' NO es fatal: durante el emparejamiento Baileys reinicia el
          // stream (error 515) y emite close→open. La autoridad del estado es
          // el polling a /status; un close transitorio no debe romper la UI.
        } catch {
          // ignore parse errors
        }
      };

      es.onerror = () => {
        // SSE con auto-reconnect; si nunca entrega, el polling cubre el caso.
      };
    },
    [cleanup, finishConnected],
  );

  // Fallback robusto e independiente del SSE/proxy/webhooks: consulta el estado
  // REAL a Evolution vía /status cada 3 s, y refresca el QR periódicamente por
  // si el SSE no entrega `qr_update` (evita escanear un código vencido). Si
  // Evolution reporta "open", el modal lo refleja aunque el SSE falle.
  const startPolling = useCallback(
    (intId) => {
      stopPolling();
      const startedAt = Date.now();
      let lastQrAt = Date.now();
      let statusFails = 0;
      const TIMEOUT_MS = 150000; // ~2.5 min sin conectar → error accionable
      const QR_REFRESH_MS = 20000;
      const MAX_STATUS_FAILS = 5; // ~15s de /status fallando seguido → error
      pollTimerRef.current = setInterval(async () => {
        if (finishedRef.current) return;
        // getUnofficialWaStatus NO lanza: señala el error con un campo { error }.
        // Por eso inspeccionamos el valor devuelto en vez de depender del catch
        // (que sería código muerto). Un fallo persistente corta antes del timeout.
        let res;
        try {
          res = await getUnofficialWaStatus(intId);
        } catch {
          res = { error: true };
        }
        if (res?.error) {
          statusFails += 1;
          if (statusFails >= MAX_STATUS_FAILS) {
            stopPolling();
            cleanup();
            setStatus("error");
            return;
          }
        } else {
          statusFails = 0; // toleramos fallos transitorios aislados
          if (res?.state === "open") {
            finishConnected();
            return;
          }
        }
        if (!finishedRef.current && Date.now() - lastQrAt > QR_REFRESH_MS) {
          lastQrAt = Date.now();
          try {
            const qr = await getUnofficialWaQr(intId);
            const b64 = qr?.qrcode?.base64;
            if (b64 && !finishedRef.current) setQrBase64(b64);
          } catch {
            // ignore: seguimos con el QR vigente
          }
        }
        if (!finishedRef.current && Date.now() - startedAt > TIMEOUT_MS) {
          stopPolling();
          cleanup();
          setStatus("error");
        }
      }, 3000);
    },
    [stopPolling, cleanup, finishConnected],
  );

  const handleCreate = async () => {
    if (!displayName.trim()) {
      showToast(t("Ingresa un nombre para este número"), false);
      return;
    }
    setStatus("creating");

    const result = await createUnofficialWaInstance(
      displayName.trim(),
      selectedGroupId || undefined,
      purpose,
    );
    if (result?.ok) {
      const integ = result.integration;
      setIntegrationId(integ.id);
      setInstanceName(integ.evolution_instance_name);

      if (result.qrcode?.base64) {
        setQrBase64(result.qrcode.base64);
      }
      setStatus("scanning");
      finishedRef.current = false;
      connectSSE(integ.id);
      startPolling(integ.id);
    } else {
      showToast(
        typeof result?.error === "string" ? result.error : t("Error al crear instancia"),
        false,
      );
      setStatus("error");
    }
  };

  const canClose = status !== "creating" && status !== "connected";
  const modalTitle =
    status === "connected"
      ? t("WhatsApp conectado")
      : status === "error"
      ? t("No se pudo conectar")
      : t("Conectar WhatsApp");

  return (
    <Modal
      open={isOpen}
      onClose={() => canClose && toggle()}
      hideClose={!canClose}
      size="sm"
      /* A6 · Sin `title` el primitivo pinta igual la banda de cabecera (borde
         + padding) solo para alojar la X: 72px en blanco arriba del modal, con
         el título real 200px más abajo dentro del cuerpo. En móvil, donde ese
         desperdicio duele (y el teclado está por abrirse), el título sube a la
         cabecera y el h3 del cuerpo desaparece. En escritorio nada cambia. */
      title={isMobile ? modalTitle : undefined}
      ariaLabel={t("Conectar WhatsApp")}
    >
      {/* Icon header */}
      <div className="flex items-center justify-center w-11 h-11 rounded-full bg-brand-50 text-brand-600 mb-3 mx-auto">
        {status === "connected" ? (
          <CheckCircle2 className="w-5 h-5" aria-hidden="true" />
        ) : status === "error" ? (
          <AlertTriangle className="w-5 h-5 text-status-error" aria-hidden="true" />
        ) : (
          <QrCode className="w-5 h-5" aria-hidden="true" />
        )}
      </div>

      {/* En escritorio el título sigue viviendo en el cuerpo, exactamente donde
          estaba, para no mover nada de ≥768px. */}
      {!isMobile && (
        <h3 id="qr-modal-title" className="text-base font-semibold text-fg text-center">
          {modalTitle}
        </h3>
      )}

      {/* ── Estados ─────────────────────────────────────────────── */}

      {status === "idle" && (
        <>
          <p className="mt-2 text-sm text-fg-muted text-center leading-relaxed">
            {t("Ingresa un nombre para identificar este número y opcionalmente asígnalo a un grupo de negocio.")}
          </p>
          <div className="mt-4 space-y-2">
            <input
              type="text"
              placeholder={t("Nombre del número (ej. Ventas Principal)")}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={60}
              autoFocus
              className="w-full h-11 md:h-9 px-3 rounded-md border border-border bg-bg text-base md:text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            {groups?.length > 0 && (
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="w-full h-11 md:h-9 px-3 rounded-md border border-border bg-bg text-base md:text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">{t("Sin grupo")}</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            )}

            {/* Propósito del número */}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">
                {t("¿Para qué usarás este número?")}
              </div>
              {/* Mismo criterio que UnofficialWaCard: bajo md la fila de tres
                  segmentos no cabe a 360px y el overflow-hidden del grupo
                  cortaba la etiqueta del medio. */}
              <div
                className="grid grid-cols-1 w-full md:inline-flex md:w-auto rounded-md border border-border overflow-hidden"
                role="group"
                aria-label={t("Uso de este número")}
              >
                {PURPOSE_OPTIONS.map((opt, idx) => {
                  const active = purpose === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setPurpose(opt.value)}
                      className={cn(
                        "px-1.5 md:px-3 min-h-[44px] md:min-h-0 md:h-8 text-[12px] md:text-xs leading-tight font-medium transition-colors",
                        idx > 0 && "border-t md:border-t-0 md:border-l border-border",
                        active
                          ? "hd-brand-solid bg-brand-500 text-white"
                          : "bg-bg text-fg-muted hover:bg-bg-muted hover:text-fg",
                      )}
                    >
                      {t(opt.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* El mismo aviso que la tarjeta de la que sale este modal: vincular
              por QR queda fuera de la API oficial de WhatsApp y un mal uso
              puede costar el número. Va antes de "Generar QR" —después ya es
              tarde para advertir— y con las MISMAS claves i18n. */}
          <div
            role="note"
            className="mt-3 flex items-start gap-2.5 rounded-lg border border-status-warning/30 bg-status-warning-soft/60 px-3 py-2.5"
          >
            <AlertTriangle
              className="w-4 h-4 shrink-0 mt-0.5 text-status-warning"
              aria-hidden="true"
            />
            <p className="text-[12px] md:text-[11px] leading-relaxed text-fg text-left">
              <strong className="font-semibold text-fg">
                {t("Conexión fuera de la API oficial:")}{" "}
              </strong>
              {t(
                "este número se conecta vía QR, fuera de la API oficial de WhatsApp. Un mal uso de la aplicación (spam, envíos masivos o contenido que infrinja los términos de WhatsApp) puede provocar que tu número sea baneado y pierdas este número de forma definitiva. Úsalo de forma correcta.",
              )}
            </p>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={toggle}>
              {t("Cancelar")}
            </Button>
            <Button
              className="hd-brand-solid"
              size="sm"
              onClick={handleCreate}
              disabled={!displayName.trim()}
              iconLeft={<QrCode className="w-3.5 h-3.5" aria-hidden="true" />}
            >
              {t("Generar QR")}
            </Button>
          </div>
        </>
      )}

      {status === "creating" && (
        <div className="mt-4 flex flex-col items-center gap-3 py-6 text-sm text-fg-muted">
          <Loader2 className="w-6 h-6 animate-spin text-brand-600" aria-hidden="true" />
          {t("Creando instancia…")}
        </div>
      )}

      {status === "scanning" && (
        <>
          <p className="mt-2 text-sm text-fg-muted text-center leading-relaxed">
            {t("Abre WhatsApp en tu teléfono, ve a")}{" "}
            <strong className="text-fg">{t("Dispositivos vinculados")}</strong>{" "}
            {t("y escanea este código QR.")}
          </p>

          <div className="mt-4 flex flex-col items-center">
            <div className="relative w-56 h-56 rounded-lg border border-border bg-bg-muted/30 flex items-center justify-center overflow-hidden">
              {qrBase64 ? (
                <img
                  src={qrBase64.startsWith("data:") ? qrBase64 : `data:image/png;base64,${qrBase64}`}
                  alt={t("Código QR para vincular WhatsApp")}
                  className="w-full h-full object-contain p-2"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-fg-muted text-xs">
                  <Loader2 className="w-5 h-5 animate-spin text-brand-600" aria-hidden="true" />
                  {t("Generando QR…")}
                </div>
              )}
            </div>

            <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-fg-subtle">
              <Smartphone className="w-3 h-3" aria-hidden="true" />
              {t("El código se refresca solo y detectamos la conexión automáticamente.")}
            </div>
          </div>

          <div className="mt-4 flex justify-center">
            <Button variant="ghost" size="sm" onClick={toggle}>
              {t("Cancelar")}
            </Button>
          </div>
        </>
      )}

      {status === "connected" && (
        <div className="mt-3 flex flex-col items-center gap-3 py-6">
          <div
            className={cn(
              "w-12 h-12 rounded-full bg-status-success-soft text-status-success flex items-center justify-center",
              "animate-in",
            )}
          >
            <CheckCircle2 className="w-6 h-6" aria-hidden="true" />
          </div>
          <p className="text-sm text-fg text-center">
            {t("Tu número quedó conectado. Cerrando…")}
          </p>
        </div>
      )}

      {status === "error" && (
        <>
          <p className="mt-2 text-sm text-fg-muted text-center leading-relaxed">
            {t("No pudimos completar la vinculación. Verifica tu conexión y vuelve a intentarlo.")}
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={toggle}>
              {t("Cerrar")}
            </Button>
            <Button className="hd-brand-solid" size="sm" onClick={() => setStatus("idle")}>
              {t("Reintentar")}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
